import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {BehaviorSubject, Observable, shareReplay} from 'rxjs';
import { map } from 'rxjs/operators';
import { LoginRequest, LoginTypeEnum } from '../requests/LoginRequest';
import { RegisterRequest } from '../requests/RegisterRequest';
import { RefreshRequest } from '../requests/RefreshRequest';
import { LogoutResponse } from '../responses/LogoutResponse';
import {
  Router,
} from '@angular/router';
import { ResetRequest } from '../requests/ResetRequest';
import { LoginResponse } from '../responses/LoginResponse';
import { EndpointService } from './endpoint.service';
import { UserAuthenticatedDTO } from '../requests/UserAuthenticatedDTO';
import { ENV, Role } from '../helpers/constants';
import { BaseResponse } from '../responses/BaseResponse';
import { GetUserCompanyInfoResponse } from '../responses/GetUserCompanyInfoResponse';
import { SubdomainInfo } from '../models/subdomain-info';
import { UserService } from './user.service';
import { User } from '../models';
import { TimeTrackerService } from './time-tracker.service';
import { getEnvironment } from './environment.service';
import { CompanyLoginDTO } from '../requests/CompanyLoginDTO';
import { AuthTypeRequest } from '../requests/AuthTypeRequest';
import { CheckUserTypeResponse } from '../responses/CheckUserTypeResponse';
import { ExchangeTokenRequest } from '../requests/ExchangeTokenRequest';
import {CnpSignupCheckRequest} from '../requests/CnpSignupCheckRequest';
import {SignupRequest} from '../requests/SignupRequest';
import {TwoFactorCodeRequest, TwoFactorRequest} from "../requests/TwoFactorRequest";

declare const window: any;

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  // public
  public currentUser: Observable<UserAuthenticatedDTO>;
  public currentUserInfo: Observable<User>;
  public currentUserCompanyInfo: Observable<SubdomainInfo>;
  public isDirty = true;

  // private
  private currentUserSubject: BehaviorSubject<UserAuthenticatedDTO>;
  private currentUserInfoSubject: BehaviorSubject<User>;
  private currentUserCompanyInfoSubject: BehaviorSubject<SubdomainInfo>;
  private guidedTourCompanies = [5, 73];

  constructor(
    private _router: Router,
    private http: HttpClient,
    private userService: UserService,
    private timeTrackerService: TimeTrackerService,
  ) {
    this.currentUserSubject = new BehaviorSubject<UserAuthenticatedDTO>(
      JSON.parse(localStorage.getItem('currentUserMybenefits'))
    );
    this.currentUser = this.currentUserSubject.asObservable();
    this.currentUserInfoSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem('currentUserInfoMybenefits'))
    );
    this.currentUserInfo = this.currentUserInfoSubject.asObservable().pipe(shareReplay());
    this.currentUserCompanyInfoSubject = new BehaviorSubject<SubdomainInfo>(
      JSON.parse(localStorage.getItem('currentUserCompanyInfoMybenefits'))
    );
    this.currentUserCompanyInfo =
      this.currentUserCompanyInfoSubject.asObservable();


    window.addEventListener('storage', (event: StorageEvent) => {
      if (!(event.key === 'currentUserInfoMybenefits')) {
        return;
      }
      if (event.newValue == null) {
        this.logout().subscribe();
      } else {
        window.location.reload();
      }
    });
  }

  // getter: currentUserValue
  public get currentUserValue(): UserAuthenticatedDTO {
    return this.currentUserSubject.value;
  }

  // getter: getDashboardInfo
  public get currentUserInfoValue(): User {
    return this.currentUserInfoSubject.value;
  }

  // getter: getCompanyInfo
  public get currentUserCompanyInfoValue(): SubdomainInfo {
    return this.currentUserCompanyInfoSubject.value;
  }

  register(data: RegisterRequest) {
    return this.http.post<LoginResponse>(EndpointService.register, data);
  }

  companyLogin(data: CompanyLoginDTO) {
    return this.http.request<any>('HEAD', EndpointService.companyLogin + data.subdomain);
  }

  login(data: LoginRequest) {
    return this.http.post<any>(EndpointService.login, data)
      .pipe(map((response: LoginResponse) => this.handleLoginResponse(response, true)));
  }

  verifyTwoFactorAuthentication(data: TwoFactorRequest) {
    return this.http.post<LoginResponse>(EndpointService.verifyTwoFactor, data)
      .pipe(map((response: LoginResponse) => this.handleLoginResponse(response, true)));
  }

  resendTwoFactorCode(data: TwoFactorCodeRequest) {
    return this.http.post<any>(EndpointService.resendTwoFactorCode, data)
  }

  handleLoginResponse(response: LoginResponse, checkConsent:boolean = false, newEmail: string = '') {
    const user: UserAuthenticatedDTO = {
      ...response.responseObject,
      loginType: LoginTypeEnum.Email
    };

    if(newEmail && newEmail.trim() !== '') {
      user.email = newEmail.trim();
    }

    // login successful if there's a jwt token in the response
    if (user && user.accessToken) {
      if (checkConsent) {
        if (user.latestConsentAccepted) {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          localStorage.setItem('currentUserMybenefits', JSON.stringify(user));
          this.currentUserSubject.next(user);
          this.updateCurrentUserInfo();
        }
      } else {
        localStorage.setItem('currentUserMybenefits', JSON.stringify(user));
        this.currentUserSubject.next(user);
        this.updateCurrentUserInfo();

      }
    }
    return user;
  }

  storeUser(user: UserAuthenticatedDTO) {
    localStorage.setItem('currentUserMybenefits', JSON.stringify(user));
    this.currentUserSubject.next(user);
    this.updateCurrentUserInfo();
  }

  loginSSO(data: ExchangeTokenRequest) {
    return this.http.post(`${EndpointService.tokenExchange}`, data)
      .pipe(map((response: LoginResponse) => this.handleLoginResponse(response, true)));
  }

  logout() {
    return this.http.delete<LogoutResponse>(EndpointService.logout).pipe(
      map((response: LogoutResponse) => {
        if (response.isSuccess) {
          // remove user from local storage to log user out
          localStorage.removeItem('currentUserMybenefits');
          localStorage.removeItem('currentUserInfoMybenefits');
          localStorage.removeItem('currentUserCompanyInfoMybenefits');
          const subdomain = localStorage.getItem(
            'currentUserSubdomainMybenefits'
          );
          // localStorage.removeItem('currentUserSubdomainMybenefits');
          // notify
          this.currentUserSubject.next(null);
          this._router.navigate(['/' + subdomain + '/authentication/login']);
        }
      })
    );
  }

  refreshTokenExpired() {
    // remove user from local storage to log user out
    localStorage.removeItem('currentUserMybenefits');
    localStorage.removeItem('currentUserInfoMybenefits');
    localStorage.removeItem('currentUserCompanyInfoMybenefits');
    const subdomain = localStorage.getItem('currentUserSubdomainMybenefits');
    // localStorage.removeItem('currentUserSubdomainMybenefits');
    // notify
    this.currentUserSubject.next(null);
    this._router.navigate(['/' + subdomain + '/authentication/login']);
  }

  refreshToken(data: RefreshRequest) {
    return this.http.post<BaseResponse>(EndpointService.refresh, data);
  }

  forgotPassword(data: LoginRequest) {
    return this.http.post<BaseResponse>(EndpointService.forgotPassword, data);
  }

  resetPassword(data: ResetRequest) {
    return this.http.patch<BaseResponse>(EndpointService.resetPassword, data);
  }

  getUserCompanyInfo(subdomain: string) {
    return this.http.get<GetUserCompanyInfoResponse>(EndpointService.getUserCompanyInfo(subdomain), {})
      .pipe(
        map((response: GetUserCompanyInfoResponse) => {
          // we got a valid response, let's set the subdomain in local storage
          const subdomainFromLS = localStorage.getItem(
            'currentUserSubdomainMybenefits'
          );
          if (subdomain !== 'default') {
            localStorage.setItem('currentUserSubdomainMybenefits', subdomain);
          } else {
            if (!subdomainFromLS) {
              localStorage.setItem('currentUserSubdomainMybenefits', 'default');
            }
          }

          const companyInfo: SubdomainInfo = response.responseObject;
          localStorage.setItem(
            'currentUserCompanyInfoMybenefits',
            JSON.stringify(companyInfo)
          );
          this.currentUserCompanyInfoSubject.next(companyInfo);
          return companyInfo;
        })
      );
  }

  getUserAuthType(data: AuthTypeRequest){
    return this.http.get<CheckUserTypeResponse>(EndpointService.getUserAuthType(data.subdomain, data.email), {})
      .pipe(
        map((response: CheckUserTypeResponse) => response.responseObject)
      );
  }

  updateCurrentUserInfo() {
    if (this.currentUserValue) {
      this.userService.getUserProfile(this.currentUserValue.userId).subscribe(
        (userInfoResponse) => {
          localStorage.setItem(
            'currentUserInfoMybenefits',
            JSON.stringify(userInfoResponse.responseObject)
          );
          this.currentUserInfoSubject.next(userInfoResponse.responseObject);
          if (getEnvironment() === ENV.DEMO && userInfoResponse.responseObject.companyId && this.guidedTourCompanies.includes(userInfoResponse.responseObject.companyId)) {
            const createdOn = new Date(userInfoResponse.responseObject.userMetadata.createdTimestamp);
            const timeOffsetInMS = createdOn.getTimezoneOffset() * -60000;
            createdOn.setTime(createdOn.getTime() + timeOffsetInMS);
            window.userGuiding.identify(userInfoResponse.responseObject.id, {
              email: userInfoResponse.responseObject.email,
              name: userInfoResponse.responseObject.firstName + userInfoResponse.responseObject.lastName,
              created_at: createdOn
            });
          };
        },
        (error) => { }
      );
    }
  }

  performCnpSignupCheck(data: CnpSignupCheckRequest) {
    return this.http.post(EndpointService.cnpSignUpCheck, data);
  }

  completeSignUp(data: SignupRequest) {
    return this.http.post(EndpointService.completeSignUp, data);
  }
}
